import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Check, Radio, Share2, Trash2 } from "lucide-react";
import {
  useLiveStream,
  useLiveStreamHistory,
  useUpdateLiveStream,
  useDeleteLiveStream,
} from "@/hooks/useLiveStream";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { LatencyBadge } from "@/components/liveStream/LatencyBadge";
import { LiveStreamHistory } from "@/components/liveStream/LiveStreamHistory";
import { LiveStreamPlayer } from "@/components/liveStream/LiveStreamPlayer";
import { LiveStreamStatusBadge } from "@/components/liveStream/LiveStreamStatusBadge";
import LiveStreamEndedSummary from "@/components/session/LiveStreamEndedSummary";
import { handleApiError } from "@/utils/errorHandler";
import type { ApiError } from "@/utils/errorHandler";
import type {
  LiveStream,
  LiveStreamDisplayStatus,
  LiveStreamState,
  Session,
} from "@/types";

/** Fallback for responses that predate the server-derived display status */
const legacyDisplayStatus: Record<LiveStreamState, LiveStreamDisplayStatus> = {
  planned: "planned",
  on_air: "live",
  ended: "ended",
  deleted: "deleted",
};

/** Returns true when the stream is available for viewing (stream not ended or deleted) */
function isStreamActive(status: LiveStreamDisplayStatus): boolean {
  return status !== "ended" && status !== "deleted";
}

function synchronizeLiveStreamHistory(
  history: LiveStream[],
  historyFetchedAt: number,
  currentStream?: LiveStream,
  currentStreamFetchedAt = 0,
): LiveStream[] {
  const attemptsById = new Map(
    history.map((attempt) => [
      attempt.id,
      { attempt, fetchedAt: historyFetchedAt },
    ]),
  );

  if (currentStream) {
    if (currentStreamFetchedAt >= historyFetchedAt) {
      attemptsById.set(currentStream.id, {
        attempt: currentStream,
        fetchedAt: currentStreamFetchedAt,
      });
    }
  }

  return Array.from(attemptsById.values(), ({ attempt }) => attempt).sort(
    (left, right) => {
      const createdAtDifference =
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime();
      return createdAtDifference || right.id.localeCompare(left.id);
    },
  );
}

interface SessionLiveStreamSectionProps {
  session: Session;
  sessionId: string;
  isOwner: boolean;
}

export default function SessionLiveStreamSection({
  session,
  sessionId,
  isOwner,
}: SessionLiveStreamSectionProps) {
  const { t, i18n } = useTranslation(["pages"]);

  const [isDeleteStreamConfirmOpen, setIsDeleteStreamConfirmOpen] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const shareLinkCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: liveStream,
    dataUpdatedAt: liveStreamFetchedAt,
    isLoading: liveStreamLoading,
  } = useLiveStream(sessionId);
  const updateLiveStream = useUpdateLiveStream(sessionId);
  const deleteLiveStream = useDeleteLiveStream(sessionId);
  const {
    data: liveStreamHistory = [],
    dataUpdatedAt: liveStreamHistoryFetchedAt,
  } = useLiveStreamHistory(
    sessionId,
    isOwner && !liveStreamLoading,
    liveStream?.id,
  );

  const synchronizedLiveStreamHistory = useMemo(
    () =>
      synchronizeLiveStreamHistory(
        liveStreamHistory,
        liveStreamHistoryFetchedAt,
        liveStream,
        liveStreamFetchedAt,
      ),
    [liveStreamHistory, liveStreamHistoryFetchedAt, liveStream, liveStreamFetchedAt],
  );

  const liveStreamDisplayStatus: LiveStreamDisplayStatus =
    liveStream?.displayStatus ??
    legacyDisplayStatus[liveStream?.state ?? "planned"];

  if (!session.rtspUrl && !session.viewerUrl) {
    return null;
  }

  const handleOpenViewerWindow = () => {
    // Opens the in-app public watch page (Task 7, #506) rather than the raw
    // MediaMTX viewerUrl directly, so it gets the immersive player, controls,
    // and non-playing states for free.
    window.open(`/watch/${sessionId}`, "_blank", "noopener,noreferrer");
  };

  const handleCopyShareLink = () => {
    const shareUrl = `${window.location.origin}/watch/${session.id}`;
    setShareLinkCopied(true);
    if (shareLinkCopiedTimerRef.current !== null) {
      clearTimeout(shareLinkCopiedTimerRef.current);
    }
    shareLinkCopiedTimerRef.current = setTimeout(
      () => setShareLinkCopied(false),
      2000,
    );
    void navigator.clipboard.writeText(shareUrl).catch(() => undefined);
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-secondary-200 mb-8">
        <div className="p-4 sm:p-6 border-b border-secondary-100">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-secondary-900">{t("sessions.detail.liveStream.title", { ns: "pages" })}</h3>
              {liveStreamDisplayStatus === "ended" ? (
                <span className="inline-flex items-center rounded-full border border-secondary-200 bg-secondary-50 px-2.5 py-1 text-xs font-medium text-secondary-600">
                  {t("sessions.detail.liveStream.notLive", { ns: "pages" })}
                </span>
              ) : (
                <LiveStreamStatusBadge status={liveStreamDisplayStatus} />
              )}
            </div>
            <div className="flex items-center gap-2">
              {session.viewerUrl && isStreamActive(liveStreamDisplayStatus) && (
                <button
                  onClick={handleOpenViewerWindow}
                  className="p-1.5 text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100 rounded transition-colors"
                  aria-label={t("sessions.detail.liveStream.openViewerWindow", { ns: "pages" })}
                >
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              )}
              {isOwner && !liveStreamLoading && (
                <div className="flex items-center gap-2">
                  {session.visibility === "public" && (
                    <button
                      onClick={handleCopyShareLink}
                      className="p-1.5 text-secondary-400 hover:text-secondary-700 hover:bg-secondary-100 rounded transition-colors"
                      aria-label={t(
                        shareLinkCopied
                          ? "sessions.detail.liveStream.linkCopied"
                          : "sessions.detail.liveStream.copyLink",
                        { ns: "pages" },
                      )}
                    >
                      {shareLinkCopied ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Share2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {liveStream && isStreamActive(liveStreamDisplayStatus) && (
                    <>
                      {liveStream.state === "planned" && (
                        <button
                          onClick={() => updateLiveStream.mutate({ streamId: liveStream.id, state: "on_air" })}
                          disabled={updateLiveStream.isPending}
                          className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-sm whitespace-nowrap"
                        >
                          <Radio className="h-3.5 w-3.5" />
                          {updateLiveStream.isPending
                            ? t("sessions.detail.liveStream.starting", { ns: "pages" })
                            : t("sessions.detail.liveStream.goLive", { ns: "pages" })}
                        </button>
                      )}
                      <button
                        onClick={() => setIsDeleteStreamConfirmOpen(true)}
                        className="p-1.5 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        aria-label={t("sessions.detail.liveStream.delete", { ns: "pages" })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {liveStream?.description && (
            <p className="text-sm text-secondary-600 mb-4">{liveStream.description}</p>
          )}
          {liveStreamDisplayStatus === "failed" && liveStream?.deviceStatus?.errorMessage && (
            <p className="text-sm text-red-600 mb-4" role="alert">
              {liveStream.deviceStatus.errorMessage}
            </p>
          )}
          {/* Only show live viewer details when stream is active (not ended or deleted) */}
          {isStreamActive(liveStreamDisplayStatus) ? (
            <div>
              {liveStreamDisplayStatus === "live" && (
                <div className="mb-3">
                  <LatencyBadge sessionId={session.id} />
                </div>
              )}
              {session.viewerUrl && (
                <div className="mx-[calc(var(--shell-gutter-x)*-1)] sm:mx-0">
                  <LiveStreamPlayer
                    src={session.whepUrl ?? session.viewerUrl}
                    title={t("sessions.detail.liveStream.viewerTitle", {
                      ns: "pages",
                      sessionTitle: session.title,
                    })}
                    startTime={liveStream?.startedAt}
                    isRunning={liveStreamDisplayStatus === "live"}
                    variant="inline"
                  />
                </div>
              )}
            </div>
          ) : liveStreamDisplayStatus === "ended" ? (
            <div>
              <p className="font-medium text-secondary-700">
                {t("sessions.detail.liveStream.noActive", { ns: "pages" })}
              </p>
              <LiveStreamEndedSummary
                stream={liveStream}
                messageKey="sessions.detail.liveStream.lastEnded"
                className="mt-1 text-sm text-secondary-500"
                fallbackClassName="mt-1 text-sm text-secondary-500"
              />
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3">
              <LiveStreamEndedSummary
                stream={liveStream}
                messageKey="sessions.detail.liveStream.unavailable"
                fallbackClassName="text-sm text-secondary-500 italic"
              />
            </div>
          )}
        </div>
        {isOwner && (
          <LiveStreamHistory
            attempts={synchronizedLiveStreamHistory}
            locale={i18n.language}
          />
        )}
      </div>
      <ConfirmationModal
        isOpen={isDeleteStreamConfirmOpen}
        onClose={() => setIsDeleteStreamConfirmOpen(false)}
        onConfirm={async () => {
          if (liveStream) {
            try {
              await deleteLiveStream.mutateAsync(liveStream.id);
              setIsDeleteStreamConfirmOpen(false);
            } catch (error) {
              handleApiError(error as ApiError, t("sessions.detail.errors.deleteLiveStream", { ns: "pages" }));
            }
          }
        }}
        title={t("sessions.detail.liveStream.deleteTitle", { ns: "pages" })}
        message={t("sessions.detail.liveStream.deleteMessage", { ns: "pages" })}
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={deleteLiveStream.isPending}
      />
    </>
  );
}
